import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getFirestore, collection, query, orderBy, onSnapshot, where } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { listenToUserData } from '../services/userService';
import './WallPanel.css';

const WallPanel = () => {
  const db = getFirestore();
  const auth = getAuth();

  const [message, setMessage] = useState("");
  const [posts, setPosts] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [selectedAnon, setSelectedAnon] = useState(false);

  const MAX_CHARS = 140;

const formatTime = (timestamp) => {
  if (!timestamp) return "";
  const date = timestamp.toDate();
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
};
  
  useEffect(() => {
    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const q = query(collection(db, "Wall"), where("createdAt", ">", yesterday), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPosts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [db]);

  useEffect(() => {
    if (!auth.currentUser) return;
    return listenToUserData(auth.currentUser.uid, (data) => setUserData(data));
  }, [auth.currentUser]);

  const currentFee = Math.max(10, Math.floor((userData?.userPortfolioValue || 0) * (selectedAnon ? 0.0006 : 0.0001)));
  const canAfford = (userData?.userMoney || 0) >= currentFee;

  const handlePostRequest = async () => {
    if (!canAfford || isProcessing) return;
    setIsProcessing(true);
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch("https://us-central1-stockmarket-83a73.cloudfunctions.net/postToWall", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${idToken}` },
        body: JSON.stringify({ message: message.trim(), isAnonymous: selectedAnon })
      });
      if (response.ok) {
        setMessage("");
        setShowConfirm(false);
        setSelectedAnon(false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="wall-container">
      <div className="wall-input-area compact-input">
        <textarea 
          value={message} 
          onChange={(e) => setMessage(e.target.value)} 
          placeholder="Type a message..." 
          maxLength={MAX_CHARS}
          rows={1}
        />
        <div className="input-footer">
          <span className="char-counter">{message.length} / {MAX_CHARS}</span>
          <button 
            className="wall-post-button" 
            onClick={() => { if(message.trim()) setShowConfirm(true); }}
          >
            BROADCAST
          </button>
        </div>
      </div>

      <div className="wall-feed">
        {posts.map((post) => (
<div key={post.id} className={`wall-post-item ${post.isAnonymous ? 'anon-entry' : ''}`}>
  <div className="post-header">
    <span className="post-user-tag">
      {post.isAnonymous ? "[ANONYMOUS]" : `[${post.user}]`}
    </span>
    <span className="post-timestamp">{formatTime(post.createdAt)}</span>
  </div>
  <span className="post-text-content multi-line-text">{post.text}</span>
</div>
        ))}
      </div>

      {showConfirm && createPortal(
        <div className="wall-modal-overlay">
          <div className="floating-modal-container confirmation-style">
            <div className="draggable-title">
              <span>CONFIRM BROADCAST</span>
              <button onClick={() => setShowConfirm(false)}>×</button>
            </div>
            
            <div className="scrollable-content">
              <div className="confirmation-body">
                <p className="broadcast-preview-text multi-line-text">"{message}"</p>
                
                <div className="mode-selector">
                  <button className={`mode-btn ${!selectedAnon ? 'active' : ''}`} onClick={() => setSelectedAnon(false)}>
                    PUBLIC
                  </button>
                  <button className={`mode-btn ${selectedAnon ? 'active' : ''}`} onClick={() => setSelectedAnon(true)}>
                    ANONYMOUS
                  </button>
                </div>

                <div className="fee-readout">
                  <span className="label">TOTAL BROADCAST FEE</span>
                  <span className={`value ${!canAfford ? 'error' : ''}`}>
                    ${currentFee.toLocaleString()}
                  </span>
                </div>
              </div>
              
              <div className="modal-actions-stacked">
                <button className="settings-button cancel" onClick={handlePostRequest} disabled={isProcessing || !canAfford}>
                  {isProcessing ? "TRANSMITTING..." : "CONFIRM"}
                </button>
                <button className="settings-button confirm" onClick={() => setShowConfirm(false)}>
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default WallPanel;